import React, { useEffect, useMemo, useState } from 'react';
import { CreditCard, Plus, Search, Edit, Trash2, ToggleLeft, ToggleRight, RefreshCcw } from 'lucide-react';
import { PaymentType } from '../types/payment';
import { paymentTypeService } from '../services/paymentTypeService';
import alertService from '../services/alertService';
import PaymentTypeModal from '../components/payment/PaymentTypeModal';

const PaymentTypesPage: React.FC = () => {
  const [items, setItems] = useState<PaymentType[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<PaymentType | null>(null);

  const filtered = useMemo(() => {
    // Remover entradas nulas/indefinidas para evitar erros ao acessar propriedades
    const list = items.filter(Boolean).filter((t) => {
      const matchesSearch = search
        ? (t.nome?.toLowerCase().includes(search.toLowerCase()) ||
           (t.descricao || '').toLowerCase().includes(search.toLowerCase()))
        : true;
      const matchesActive = onlyActive ? !!t.ativo : true;
      return matchesSearch && matchesActive;
    });
    return list.sort((a, b) => {
      const ao = a.ordem ?? Number.MAX_SAFE_INTEGER;
      const bo = b.ordem ?? Number.MAX_SAFE_INTEGER;
      if (ao === bo) return (a.nome || '').localeCompare(b.nome || '');
      return ao - bo;
    });
  }, [items, search, onlyActive]);

  const load = async () => {
    try {
      setLoading(true);
      const res = await paymentTypeService.list();
      const raw: any = res?.data;
      const list: PaymentType[] = Array.isArray(raw) ? raw : (raw?.content || []);
      setItems(list.filter(Boolean));
    } catch (error) {
      alertService.error('Erro ao carregar tipos de pagamento');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCreate = () => {
    setEditing(null);
    setShowModal(true);
  };

  const handleEdit = (item: PaymentType) => {
    setEditing(item);
    setShowModal(true);
  };

  const handleDelete = async (item: PaymentType) => {
    const confirm = await alertService.confirm('Excluir tipo de pagamento?', `Esta ação não pode ser desfeita. Tipo: ${item.nome}`);
    if (!confirm.isConfirmed) return;
    try {
      alertService.loading('Excluindo...');
      await paymentTypeService.delete(item.id);
      alertService.close();
      alertService.success('Tipo de pagamento excluído');
      setItems((prev) => prev.filter((x) => x.id !== item.id));
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao excluir tipo de pagamento');
    }
  };

  const handleToggleActive = async (item: PaymentType) => {
    try {
      const action = item.ativo ? 'desativar' : 'ativar';
      alertService.loading(`${item.ativo ? 'Desativando' : 'Ativando'}...`);
      const res = item.ativo
        ? await paymentTypeService.deactivate(item.id)
        : await paymentTypeService.activate(item.id);
      alertService.close();
      setItems((prev) => prev.map((x) => (x.id === item.id ? res.data : x)));
      alertService.success(`Tipo de pagamento ${item.ativo ? 'desativado' : 'ativado'}`);
    } catch (error) {
      alertService.close();
      alertService.error('Erro ao alterar status');
    }
  };

  const handleSaved = (saved: PaymentType) => {
    setItems((prev) => {
      const exists = prev.some((x) => x.id === saved.id);
      return exists ? prev.map((x) => (x.id === saved.id ? saved : x)) : [saved, ...prev];
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CreditCard className="h-6 w-6 text-red-600" />
          <h1 className="text-xl font-semibold text-gray-900">Tipos de Pagamento</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="inline-flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg"
            onClick={load}
            disabled={loading}
            title="Recarregar"
          >
            <RefreshCcw className="h-4 w-4" />
            Recarregar
          </button>
          <button
            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg"
            onClick={handleCreate}
          >
            <Plus className="h-4 w-4" />
            Novo Tipo
          </button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-4 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="flex items-center gap-2 border rounded-lg px-3 py-2">
              <Search className="h-4 w-4 text-gray-500" />
              <input
                className="w-full outline-none"
                placeholder="Buscar por nome ou descrição"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <label className="inline-flex items-center gap-2 px-2 py-2">
              <input
                type="checkbox"
                checked={onlyActive}
                onChange={(e) => setOnlyActive(e.target.checked)}
              />
              Mostrar apenas ativos
            </label>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gray-50">
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Nome</th>
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Ordem</th>
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Descrição</th>
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Status</th>
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Pagamento imediato</th>
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Parcelamento</th>
                <th className="text-left text-sm font-medium text-gray-700 px-4 py-2">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">Carregando...</td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-6 text-center text-gray-500">Nenhum tipo encontrado</td>
                </tr>
              ) : (
                filtered.map((item) => (
                  <tr key={item.id} className="border-t">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900">{item.nome}</div>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{item.ordem ?? '-'}</td>
                    <td className="px-4 py-2 text-gray-700">{item.descricao || '-'}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${item.ativo ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                        {item.ativo ? 'ATIVO' : 'INATIVO'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-700">{item.pagamentoImediato ? 'Sim' : 'Não'}</td>
                    <td className="px-4 py-2 text-gray-700">
                      {item.permiteParcelamento ? `Sim (${item.maxParcelas || 0}x)` : 'Não'}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <button
                          className="inline-flex items-center gap-1 px-2 py-1 border rounded hover:bg-gray-50"
                          onClick={() => handleEdit(item)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                          Editar
                        </button>
                        <button
                          className="inline-flex items-center gap-1 px-2 py-1 border rounded hover:bg-gray-50"
                          onClick={() => handleToggleActive(item)}
                          title={item.ativo ? 'Desativar' : 'Ativar'}
                        >
                          {item.ativo ? <ToggleLeft className="h-4 w-4" /> : <ToggleRight className="h-4 w-4" />}
                          {item.ativo ? 'Desativar' : 'Ativar'}
                        </button>
                        <button
                          className="inline-flex items-center gap-1 px-2 py-1 border rounded hover:bg-gray-50 text-red-600"
                          onClick={() => handleDelete(item)}
                          title="Excluir"
                        >
                          <Trash2 className="h-4 w-4" />
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <PaymentTypeModal
          paymentType={editing || undefined}
          onClose={() => setShowModal(false)}
          onSave={handleSaved}
        />
      )}
    </div>
  );
};

export default PaymentTypesPage;